// src/pages/PrivacyPolicy.jsx
import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaCopy,
  FaDownload,
  FaChevronDown,
  FaCheck,
  FaShieldAlt,
} from "react-icons/fa";

const SECTIONS = [
  {
    id: "overview",
    title: "Overview",
    body:
      "We collect only the data required to provide our services, including your name, contact information and service requirements. We never sell personal information. Data is used to communicate with you, deliver projects and meet legal or billing requirements.",
  },
  {
    id: "what",
    title: "What We Collect",
    body:
      "• Name\n• Phone / Email\n• Service requirements and project files\n• Optional billing address for invoices\n\nWe do not collect sensitive personal data unless explicitly required and agreed in writing.",
  },
  {
    id: "why",
    title: "Why We Use It",
    body:
      "We use your information to communicate about projects, deliver and maintain services, fulfill contracts, process invoices, prevent fraud and comply with applicable legal obligations.",
  },
  {
    id: "cookies",
    title: "Cookies & Tracking",
    body:
      "We may use cookies to improve your website experience, understand usage patterns and analyze traffic. You can accept or decline optional cookies using the cookie preferences below.",
  },
  {
    id: "security",
    title: "Security & Retention",
    body:
      "We take reasonable measures to protect your information using appropriate security practices. Project data may be retained for the duration of the business relationship and for periods required by applicable legal or accounting obligations.",
  },
  {
    id: "contact",
    title: "Contact & Requests",
    body:
      "To request access, correction, export or deletion of your information, contact us at primeappconnect@gmail.com or use the Contact page. We will respond to requests within a reasonable timeframe.",
  },
];

export default function PrivacyPolicy() {
  const [openId, setOpenId] = useState("overview");
  const [copiedId, setCopiedId] = useState(null);

  const [cookieChoice, setCookieChoice] = useState(() => {
    try {
      return localStorage.getItem("pappc_cookies") || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    AOS.init({
      duration: 650,
      once: true,
      easing: "ease-out-cubic",
    });
  }, []);

  const toggleSection = (id) => {
    setOpenId((current) => (current === id ? null : id));
  };

  const copySection = async (section) => {
    try {
      await navigator.clipboard.writeText(
        `${section.title}\n\n${section.body}`
      );

      setCopiedId(section.id);

      setTimeout(() => {
        setCopiedId(null);
      }, 1500);
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const downloadPolicy = () => {
    const content = SECTIONS.map(
      (section) => `${section.title}\n\n${section.body}`
    ).join("\n\n------------------------------\n\n");

    const blob = new Blob(
      [`PRIMEAPPCONNECT\nPRIVACY POLICY\n\n${content}`],
      {
        type: "text/plain;charset=utf-8",
      }
    );

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "primeappconnect-privacy-policy.txt";

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  const saveCookieChoice = (choice) => {
    setCookieChoice(choice);

    try {
      localStorage.setItem("pappc_cookies", choice);
    } catch (error) {
      console.error("Unable to save cookie preference:", error);
    }
  };

  return (
    <main className="min-h-screen bg-[#f8fafc] px-4 pb-24 pt-28 text-[#0b1220] sm:px-6">

      <div className="mx-auto max-w-4xl">

        {/* Header */}
        <header
          className="mb-8 text-center"
          data-aos="fade-down"
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-50 text-cyan-500">
            <FaShieldAlt />
          </div>

          <h1 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl">
            Privacy Policy
          </h1>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
            Clear information about how PrimeAppConnect collects, uses and
            protects your data.
          </p>
        </header>

        {/* Controls */}
        <div
          className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          data-aos="fade-up"
        >
          <div className="flex flex-wrap gap-2">

            <button
              type="button"
              onClick={() => setOpenId("all")}
              className="rounded-lg bg-gradient-to-r from-[#06b6d4] to-[#3b82f6] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              Expand All
            </button>

            <button
              type="button"
              onClick={() => setOpenId(null)}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Collapse
            </button>

            <button
              type="button"
              onClick={downloadPolicy}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <FaDownload className="text-xs text-slate-500" />
              Download
            </button>

          </div>

          <p className="text-xs text-slate-500">
            Last updated: 2026
          </p>
        </div>

        {/* Policy Sections */}
        <div className="space-y-3">

          {SECTIONS.map((section, index) => {
            const isOpen =
              openId === "all" || openId === section.id;

            return (
              <article
                key={section.id}
                data-aos="fade-up"
                data-aos-delay={index * 50}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-md"
              >

                {/* Section Header */}
                <div className="flex items-center gap-3 px-4 py-4 sm:px-5">

                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="flex min-w-0 flex-1 items-center gap-4 text-left"
                    aria-expanded={isOpen}
                    aria-controls={`privacy-${section.id}`}
                  >
                    <span className="text-xs font-bold text-slate-400">
                      0{index + 1}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-semibold text-[#0b1220]">
                        {section.title}
                      </span>

                      <span className="mt-0.5 block text-xs text-slate-500">
                        Privacy information
                      </span>
                    </span>
                  </button>

                  {/* Copy Button */}
                  <button
                    type="button"
                    onClick={() => copySection(section)}
                    title="Copy section"
                    aria-label={`Copy ${section.title}`}
                    className="hidden h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-blue-500 sm:flex"
                  >
                    {copiedId === section.id ? (
                      <FaCheck className="text-emerald-500" />
                    ) : (
                      <FaCopy />
                    )}
                  </button>

                  {/* Accordion Button */}
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    aria-label={isOpen ? "Collapse section" : "Expand section"}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-blue-500"
                  >
                    <FaChevronDown
                      className={`text-xs transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                </div>

                {/* Content */}
                <div
                  id={`privacy-${section.id}`}
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="min-h-0 overflow-hidden">

                    <div className="border-t border-slate-100 px-5 pb-5 pt-4 sm:pl-[4.25rem]">

                      <p className="whitespace-pre-line text-sm leading-7 text-slate-700">
                        {section.body}
                      </p>

                      {/* Mobile Copy */}
                      <button
                        type="button"
                        onClick={() => copySection(section)}
                        className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-blue-500 sm:hidden"
                      >
                        {copiedId === section.id ? (
                          <>
                            <FaCheck />
                            Copied
                          </>
                        ) : (
                          <>
                            <FaCopy />
                            Copy Section
                          </>
                        )}
                      </button>

                    </div>

                  </div>
                </div>

              </article>
            );
          })}

        </div>

        {/* Contact CTA */}
        <section
          data-aos="fade-up"
          className="mt-8 rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-sm sm:p-9"
        >
          <p className="text-xs font-bold tracking-[0.18em] text-cyan-500">
            HAVE A QUESTION?
          </p>

          <h2 className="mt-3 text-2xl font-bold sm:text-3xl">
            Need More Information?
          </h2>

          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-slate-600">
            If you have any questions about privacy or your personal
            information, our team is happy to help.
          </p>

          <a
            href="/contact"
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#06b6d4] to-[#3b82f6] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            Contact Us
          </a>
        </section>

        {/* Cookie Preferences */}
        {!cookieChoice && (
          <div
            data-aos="fade-up"
            className="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

              <div>
                <h3 className="text-sm font-semibold text-[#0b1220]">
                  Cookie Preferences
                </h3>

                <p className="mt-1 max-w-2xl text-xs leading-6 text-slate-500">
                  We use cookies to improve your experience and understand
                  website usage. Choose your preferred option.
                </p>
              </div>

              <div className="flex shrink-0 gap-2">

                <button
                  type="button"
                  onClick={() => saveCookieChoice("declined")}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  Decline
                </button>

                <button
                  type="button"
                  onClick={() => saveCookieChoice("accepted")}
                  className="rounded-lg bg-gradient-to-r from-[#06b6d4] to-[#3b82f6] px-4 py-2 text-xs font-semibold text-white transition hover:-translate-y-0.5"
                >
                  Accept
                </button>

              </div>

            </div>
          </div>
        )}

      </div>
    </main>
  );
}